// C57 D3: manual do operador dentro do app, em /manual, visível a todos os roles.
// Conteúdo estático no bundle (fonte canônica: MANUAL-OPERADOR.md no Drive,
// 01-clientes/hiit-fitwear/02-projetos/cataloging-platform/MANUAL-OPERADOR.md).
// Ao atualizar o manual no Drive, atualizar este arquivo também.
// v2 (23/09/2026): reescrito para a tela do produto do redesign (coluna de cores,
// Ações da foto, Sobrescrever, Tamanhos no canal, perfis configuráveis).
import type { ReactNode } from "react";
import {
  KeyRound,
  Search,
  PanelLeft,
  Image as ImageIcon,
  FileText,
  Ruler,
  Tags,
  Rocket,
  Bell,
  ShieldCheck,
  ShoppingBag,
  HelpCircle,
} from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { Card } from "../components/ui/card";

interface Section {
  id: string;
  title: string;
  icon: ReactNode;
}

const SECTIONS: Section[] = [
  { id: "entrar", title: "1. Entrar no sistema", icon: <KeyRound className="h-4 w-4" /> },
  { id: "achar-produto", title: "2. Achar um produto", icon: <Search className="h-4 w-4" /> },
  { id: "tela-produto", title: "3. A tela do produto", icon: <PanelLeft className="h-4 w-4" /> },
  { id: "fotos", title: "4. Fotos", icon: <ImageIcon className="h-4 w-4" /> },
  { id: "descricao", title: "5. Nome, descrição e categoria", icon: <FileText className="h-4 w-4" /> },
  { id: "tamanhos", title: "6. Cores e tamanhos no canal", icon: <Ruler className="h-4 w-4" /> },
  { id: "tecnicidades-seo", title: "7. Tecnicidades, SEO e preço", icon: <Tags className="h-4 w-4" /> },
  { id: "publicar", title: "8. Publicar, despublicar e republicar", icon: <Rocket className="h-4 w-4" /> },
  { id: "acompanhar", title: "9. Avisos e histórico", icon: <Bell className="h-4 w-4" /> },
  { id: "perfis", title: "10. Perfis e acessos", icon: <ShieldCheck className="h-4 w-4" /> },
  { id: "mercado-livre", title: "11. Mercado Livre", icon: <ShoppingBag className="h-4 w-4" /> },
  { id: "ml-equipe", title: "12. Mercado Livre para a equipe", icon: <ShoppingBag className="h-4 w-4" /> },
  { id: "duvidas", title: "13. Dúvidas frequentes", icon: <HelpCircle className="h-4 w-4" /> },
];

export function ManualPage(): JSX.Element {
  return (
    <div>
      <PageHeader
        title="Manual do operador"
        description="Guia rápido pra quem cadastra e publica produtos na Vitrine Hiit."
      />

      <div className="grid gap-6 lg:grid-cols-[13.75rem_1fr]" style={{ maxWidth: "64rem" }}>
        <nav
          aria-label="Seções do manual"
          className="hidden lg:block sticky top-4 self-start card"
        >
          <div className="mb-2 px-1" style={{ fontSize: "var(--fs-micro)", fontWeight: "var(--fw-bold)", textTransform: "uppercase", letterSpacing: ".05em", color: "var(--ink-3)" }}>
            Nesta página
          </div>
          <ul className="stack" style={{ gap: ".125rem" }}>
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="filters__option"
                  style={{ textDecoration: "none", color: "var(--ink)" }}
                >
                  {s.icon}
                  <span className="truncate">{s.title}</span>
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="stack" style={{ gap: "var(--sp-4)", minWidth: 0, maxWidth: "56rem" }}>
          <ManualSection id="entrar" title="1. Entrar no sistema">
            <ManualOl
              items={[
                <>Abra <ManualLink href="https://vitrine.hiitfitwear.com">vitrine.hiitfitwear.com</ManualLink> no navegador (computador ou celular).</>,
                <>Digite seu email e senha. O olhinho ao lado da senha mostra o que você digitou.</>,
                <>Clique em <ManualStrong>Entrar</ManualStrong>. Você cai no <ManualStrong>Painel</ManualStrong>, com os números do dia.</>,
              ]}
            />
            <ManualTip>
              <ManualStrong>Primeiro acesso:</ManualStrong> você recebe uma senha provisória. No
              primeiro login o sistema pede pra trocar a senha. Escolha uma senha nova e confirme.
              Pra trocar de novo depois, acesse <ManualCode>/trocar-senha</ManualCode>.
            </ManualTip>
            <ManualTip>
              <ManualStrong>Esqueci minha senha:</ManualStrong> o sistema não troca senha sozinho.
              Peça pro admin resetar a sua senha em <ManualCode>/admin/usuarios</ManualCode>.
            </ManualTip>
            <p className="text-md text-ink">
              O menu lateral fica sempre visível no computador; embaixo dele estão o botão de trocar
              o tema (claro ou escuro) e o <ManualStrong>Sair</ManualStrong>. No celular, o menu vira
              uma barra de abas no rodapé da tela.
            </p>
          </ManualSection>

          <ManualSection id="achar-produto" title="2. Achar um produto">
            <ManualUl
              items={[
                <>
                  <ManualStrong>Busca no topo:</ManualStrong> digite o código do produto (SKU) ou
                  parte do nome na caixa de busca do cabeçalho. Atalho: <ManualCode>Ctrl+K</ManualCode>{" "}
                  (no Mac, <ManualCode>⌘K</ManualCode>) leva o cursor direto pra busca. No celular,
                  use a aba <ManualStrong>Buscar</ManualStrong>.
                </>,
                <>
                  <ManualStrong>Pela lista do canal:</ManualStrong> no menu, entre no canal (ex:
                  "Catálogo do Atacado"). Abre a lista de produtos daquele canal. O botão{" "}
                  <ManualStrong>Filtros ▾</ManualStrong> filtra por status (Publicado, Rascunho,
                  Despublicado, Erro), por foto (Com foto, Sem foto) e por pendência. Clique no produto que quer editar.
                </>,
              ]}
            />
          </ManualSection>

          <ManualSection id="tela-produto" title="3. A tela do produto">
            <p className="text-md text-ink">A tela tem quatro partes:</p>
            <ManualUl
              items={[
                <>
                  <ManualStrong>Coluna de cores</ManualStrong> (à esquerda): o primeiro item é{" "}
                  <ManualStrong>Base do produto</ManualStrong>, e abaixo vem uma bolinha pra cada cor.
                  Ela acompanha a rolagem, então fica sempre à mão. O que você escolhe aqui define o
                  que aparece à direita: na Base você edita o produto inteiro; numa cor, você vê as
                  fotos e os tamanhos daquela cor.
                </>,
                <>
                  <ManualStrong>Topo:</ManualStrong> o status (Publicado, Rascunho…), os quatro selos
                  de prontidão e o botão <ManualStrong>Histórico</ManualStrong>.
                </>,
                <>
                  <ManualStrong>Conteúdo</ManualStrong> (à direita): fotos, textos, tamanhos,
                  tecnicidades, SEO e preço, um bloco embaixo do outro.
                </>,
                <>
                  <ManualStrong>Rodapé fixo:</ManualStrong> diz se o produto está pronto pra loja e
                  tem os botões de publicar (ver a seção 8).
                </>,
              ]}
            />
            <ManualTip>
              <ManualStrong>No celular:</ManualStrong> a coluna de cores vira a barra{" "}
              <ManualStrong>EDITANDO</ManualStrong> no topo, que mostra o que está aberto agora (a
              Base ou uma cor). Toque em <ManualStrong>Trocar</ManualStrong> pra escolher outra cor.
            </ManualTip>
          </ManualSection>

          <ManualSection id="fotos" title="4. Fotos">
            <p className="text-md text-ink">
              Você envia as fotos <ManualStrong>pela própria tela</ManualStrong> ou{" "}
              <ManualStrong>pelo Google Drive</ManualStrong>. Os dois valem. Em qualquer um, a foto
              aparece na ficha do produto em até <ManualStrong>7 minutos</ManualStrong>.
            </p>
            <p className="text-md font-extrabold text-ink mt-3">Enviar pela tela:</p>
            <ManualOl
              items={[
                <>
                  Abra o produto. Para uma foto geral, deixe <ManualStrong>Base do produto</ManualStrong>{" "}
                  selecionada. Para uma foto de uma cor só, escolha a cor na coluna da esquerda.
                </>,
                <>
                  Clique no cartão <ManualStrong>+ Enviar fotos</ManualStrong> (o último do grupo de
                  fotos) e escolha os arquivos, <ManualStrong>ou</ManualStrong> arraste os arquivos do
                  computador para cima do cartão de fotos. Dá pra mandar várias de uma vez (até 50).
                </>,
                <>
                  Abaixo das fotos aparece a lista <ManualStrong>Envios</ManualStrong>, com o andamento
                  de cada arquivo. <ManualStrong>Enviada · importando</ManualStrong> quer dizer que a foto
                  já está no Drive e entra na ficha sozinha em até 7 minutos. Não precisa recarregar a
                  página.
                </>,
                <>
                  A foto enviada numa cor vai para a subpasta com o nome da cor. A foto enviada na Base
                  fica solta na pasta do produto. Se a pasta do produto (ou a subpasta da cor) ainda não
                  existir, o sistema cria.
                </>,
                <>
                  Regras: <ManualStrong>JPG, PNG ou WebP</ManualStrong>, até{" "}
                  <ManualStrong>10 MB</ManualStrong> cada. Foto do iPhone em <ManualStrong>HEIC</ManualStrong>{" "}
                  é recusada: exporte como JPG ou PNG e envie de novo. Se você mandar a mesma foto duas
                  vezes para a mesma pasta, o sistema avisa <ManualStrong>Já estava no Drive</ManualStrong>{" "}
                  e não manda de novo.
                </>,
                <>
                  Produto que ainda não tem nenhuma foto: a <ManualStrong>primeira foto</ManualStrong>{" "}
                  enviada coloca o produto na loja.
                </>,
              ]}
            />
            <p className="text-md font-extrabold text-ink mt-3">Enviar pelo Drive (continua valendo do mesmo jeito):</p>
            <ManualOl
              items={[
                <>No Drive compartilhado, entre na pasta <ManualStrong>"01 - Atacado"</ManualStrong>.</>,
                <>
                  Ache a pasta do produto (nome no padrão <ManualCode>{"<código> - <nome do produto>"}</ManualCode>).
                  Se não existir, crie com esse nome. Na tela do produto, o botão{" "}
                  <ManualStrong>Abrir pasta no Drive</ManualStrong> leva direto pra ela.
                </>,
                <>
                  <ManualStrong>Foto de uma cor só:</ManualStrong> coloque numa subpasta com o nome
                  da cor, dentro da pasta do produto (ex: <ManualCode>00007 - Top Fit/Preto</ManualCode>).
                </>,
                <>
                  <ManualStrong>Foto geral do produto:</ManualStrong> deixe solta na pasta do produto.{" "}
                  <ManualStrong>Toda foto, com ou sem cor, aparece na galeria do produto na loja.</ManualStrong>{" "}
                  A foto da subpasta de uma cor vira também a imagem daquela cor, sem sair da galeria
                  do produto.
                </>,
                <>
                  Formatos aceitos: <ManualStrong>JPG e PNG de verdade</ManualStrong> (foto do iPhone
                  em HEIC é recusada).
                </>,
              ]}
            />
            <p className="text-md font-extrabold text-ink mt-3">Organizar as fotos na tela:</p>
            <ManualUl
              items={[
                <>Na Base aparecem <ManualStrong>todas</ManualStrong> as fotos do produto; a etiqueta no canto da foto mostra de qual cor ela também é. Escolha a cor na coluna da esquerda pra ver só as fotos dela.</>,
                <>Clique e arraste uma foto pra mudar a ordem. A <ManualStrong>★ estrela</ManualStrong> marca a capa do canal.</>,
                <>
                  No botão <ManualStrong>⋯</ManualStrong> de cada foto abre o menu{" "}
                  <ManualStrong>Ações da foto</ManualStrong>. Nele você:
                  <div style={{ paddingLeft: "1.25rem", marginTop: ".375rem" }}>
                    <ManualUl
                      items={[
                        <>marca a quais <ManualStrong>cores</ManualStrong> a foto pertence (dá pra marcar várias e clicar em <ManualStrong>Aplicar</ManualStrong>). Marcar cor não tira a foto da galeria do produto: ela continua na Base e vira também a foto daquela cor;</>,
                        <>clica em <ManualStrong>Tirar a cor</ManualStrong> pra ela voltar a ser só uma foto geral do produto;</>,
                        <>escolhe em quais <ManualStrong>Canais</ManualStrong> ela aparece. Foto sem o canal marcado não vai pra loja.</>,
                      ]}
                    />
                  </div>
                </>,
                <>
                  Cor sem foto nenhuma? Use <ManualStrong>Copiar fotos de…</ManualStrong> pra reaproveitar
                  as fotos de outra cor, ou clique em <ManualStrong>Enviar fotos</ManualStrong> na própria
                  cor (ou arraste os arquivos pra ela). Dá pra soltar na subpasta dela no Drive também.
                </>,
              ]}
            />
            <ManualTip>
              O que você marca na tela <ManualStrong>vale mais que a pasta</ManualStrong>. Se você
              disser que uma foto é da cor Preto, ela continua sendo do Preto mesmo que esteja em
              outra subpasta.
            </ManualTip>
            <ManualTip>
              <ManualStrong>Subpasta com nome que não bate com nenhuma cor</ManualStrong> (ex: "preto
              fosco" quando a cor no Sisplan é "Preto"): o sistema avisa e mostra as fotos num bloco à
              parte, com um seletor <ManualStrong>escolher…</ManualStrong>. Escolha a cor certa e pronto.
            </ManualTip>
          </ManualSection>

          <ManualSection id="descricao" title="5. Nome, descrição e categoria">
            <p className="text-md text-ink">
              Com <ManualStrong>Base do produto</ManualStrong> selecionada:
            </p>
            <ManualUl
              items={[
                <><ManualStrong>Nome:</ManualStrong> deixe em branco pra usar o nome que vem do Sisplan.</>,
                <><ManualStrong>Descrição curta:</ManualStrong> um resumo de 1-2 linhas.</>,
                <>
                  <ManualStrong>Descrição longa:</ManualStrong> o texto completo. Digite normal, como
                  um texto do WhatsApp. Pra separar parágrafos, deixe uma linha em branco entre eles.
                  O sistema formata certinho na loja.
                </>,
                <>
                  <ManualStrong>Categorias:</ManualStrong> escolha uma ou mais. É obrigatório ter pelo
                  menos uma pra poder publicar.
                </>,
              ]}
            />
            <p className="text-md text-ink">
              Clique em <ManualStrong>Salvar base</ManualStrong>. O que está na base vale pra todos os canais.
            </p>
            <ManualTip>
              <ManualStrong>Texto diferente só neste canal:</ManualStrong> cada campo tem a chave{" "}
              <ManualStrong>Sobrescrever</ManualStrong>. Ligada, aparece um campo pra escrever a
              versão do canal, com a base mostrada logo abaixo pra comparar. Pra desistir, clique em{" "}
              <ManualStrong>Voltar ao valor base</ManualStrong>. Grave com{" "}
              <ManualStrong>Salvar override do canal</ManualStrong>. Na categoria, sobrescrever troca
              todas as categorias da base por uma só neste canal.
            </ManualTip>
            <ManualTip>
              <ManualStrong>Gerar com IA</ManualStrong> (ícone de estrelinhas, nos campos de descrição
              e SEO) sugere um texto com base no produto. Pode levar até um minuto. O texto só é
              gravado quando você clica em salvar, então revise antes. Se a IA não responder, o motivo
              aparece em vermelho logo abaixo do botão (por exemplo, limite de uso do dia atingido ou
              serviço fora do ar). Nesse caso, escreva à mão ou tente mais tarde.
            </ManualTip>
          </ManualSection>

          <ManualSection id="tamanhos" title="6. Cores e tamanhos no canal">
            <p className="text-md text-ink">
              As cores, os tamanhos e as quantidades vêm do <ManualStrong>Sisplan</ManualStrong>. Aqui
              você só decide o que aparece na loja. Estoque 0 aparece como "esgotado" na loja.
            </p>
            <ManualUl
              items={[
                <>
                  <ManualStrong>Tamanhos no canal</ManualStrong> (na Base): desligar um tamanho aqui
                  tira esse tamanho de todas as cores neste canal.
                </>,
                <>
                  Com uma cor selecionada, você liga e desliga os tamanhos só daquela cor. Pra repetir a
                  mesma escolha em outras cores, selecione-as e use{" "}
                  <ManualStrong>Aplicar estes tamanhos às cores selecionadas</ManualStrong>.
                </>,
                <>
                  <ManualStrong>Desligar esta cor no canal</ManualStrong> esconde a cor inteira da loja
                  sem apagar nada.
                </>,
                <>
                  <ManualStrong>Ver matriz cor × tamanho</ManualStrong> mostra tudo numa tabela só, com
                  a quantidade de cada combinação.
                </>,
              ]}
            />
            <p className="text-md text-ink">
              Mudanças aqui vão pra loja na próxima publicação ou republicação.
            </p>
          </ManualSection>

          <ManualSection id="tecnicidades-seo" title="7. Tecnicidades, SEO e preço">
            <ManualUl
              items={[
                <>
                  <ManualStrong>Tecnicidades:</ManualStrong> os selos técnicos do produto (tecido,
                  proteção, compressão etc.). Marque os que valem. Ao salvar, o sistema republica o
                  produto em todos os canais sozinho.
                </>,
                <>
                  <ManualStrong>Instruções de lavagem:</ManualStrong> logo abaixo de Tecnicidades, mesmo
                  jeito de usar: marque os selos de cuidado (ex.: não usar alvejante, secar à sombra) e
                  salve. Também republica o produto em todos os canais sozinho.
                </>,
                <>
                  <ManualStrong>Guia de tamanhos:</ManualStrong> logo abaixo de Instruções de lavagem.
                  Por padrão o produto <ManualStrong>herda o guia da categoria</ManualStrong> dele (a
                  categoria mais específica vence quando o produto está em mais de uma). Pra escolher
                  outro guia ou marcar "sem guia" só neste produto, use o seletor{" "}
                  <ManualStrong>Herdar da categoria | guia | Nenhum</ManualStrong>; pra valer só num
                  canal, abra <ManualStrong>Sobrescrever no canal</ManualStrong>. A tela mostra a
                  origem do guia em uso (categoria, produto ou canal) e uma prévia da tabela já
                  filtrada aos tamanhos deste produto.
                </>,
                <>
                  <ManualStrong>SEO e preço:</ManualStrong> título e descrição que aparecem no Google
                  (com <ManualStrong>Gerar com IA</ManualStrong>) e as tags (com{" "}
                  <ManualStrong>Sugerir tags com IA</ManualStrong>).
                </>,
                <>
                  <ManualStrong>Promoção:</ManualStrong> no mesmo bloco. Informe o{" "}
                  <ManualStrong>Preço promocional</ManualStrong> e, se quiser, a data de início e de
                  fim. Clique em <ManualStrong>Salvar promoção</ManualStrong>. Ela vai pra loja na
                  próxima republicação (manual ou automática, em até 24h).{" "}
                  <ManualStrong>Limpar promoção</ManualStrong> volta ao preço normal. O preço
                  promocional precisa ser menor que o preço normal (igual não vale).
                </>,
              ]}
            />
            <ManualTip>
              O <ManualStrong>preço normal</ManualStrong> não se digita aqui: vem do Sisplan, na tabela
              do canal (o atacado usa o preço de atacado). Pra mudar, mude no Sisplan e republique.
            </ManualTip>
          </ManualSection>

          <ManualSection id="publicar" title="8. Publicar, despublicar e republicar">
            <p className="text-md text-ink">
              No topo da tela do produto ficam quatro selos: <ManualStrong>Fotos</ManualStrong>,{" "}
              <ManualStrong>Descrição</ManualStrong>, <ManualStrong>SEO</ManualStrong> e{" "}
              <ManualStrong>Grade</ManualStrong>. Verde está ok; os outros mostram o que ainda falta.
              Passe o mouse em cima pra ver o detalhe.
            </p>
            <p className="text-md font-extrabold text-ink mt-3">
              Pra publicar é obrigatório ter, ao mesmo tempo:
            </p>
            <ManualOl
              items={[
                <>Pelo menos 1 foto marcada pro canal.</>,
                <>Descrição longa preenchida (na base ou no canal).</>,
                <>Pelo menos uma categoria.</>,
              ]}
            />
            <p className="text-md text-ink">
              Se faltar algum, o rodapé fixo mostra <ManualStrong>"Falta: …"</ManualStrong> e o botão
              Publicar fica bloqueado. SEO e Grade não bloqueiam, mas um produto sem nenhuma
              combinação de cor e tamanho ligada não tem o que vender na loja.
            </p>
            <ManualUl
              items={[
                <>
                  <ManualStrong>Publicar:</ManualStrong> manda o produto pra loja. Produtos com muitas
                  cores podem levar um ou dois minutos. Quando terminar, aparece{" "}
                  <ManualStrong>Publicado</ManualStrong>.
                </>,
                <>
                  <ManualStrong>Despublicar:</ManualStrong> tira o produto da loja (ele fica como
                  rascunho). Você continua editando normalmente.
                </>,
                <>
                  <ManualStrong>Republicar:</ManualStrong> envia tudo de novo pra loja. Use depois de
                  trocar foto, texto ou tamanhos de um produto que já está publicado.
                </>,
                <>
                  <ManualStrong>Ver no WC:</ManualStrong> abre o produto na loja pra conferir.
                </>,
              ]}
            />
            <h3 className="text-md font-extrabold text-ink mt-4">8.1 Arquivar um produto</h3>
            <p className="text-md text-ink">
              Use quando o produto está ativo no Sisplan mas <ManualStrong>nunca vai entrar na vitrine</ManualStrong>. Sem isso ele
              fica como pendente pra sempre e atrapalha a fila "Para fazer hoje".
            </p>
            <ManualUl
              items={[
                <>
                  <ManualStrong>Como arquivar:</ManualStrong> na tela do produto, em <ManualStrong>Base do produto</ManualStrong>,
                  mude <ManualStrong>Situação do produto</ManualStrong> para <ManualStrong>Arquivado</ManualStrong> e confirme. O
                  aviso diz em quais canais ele está no ar. Só perfis que podem publicar mudam a situação.
                </>,
                <>
                  <ManualStrong>O que acontece:</ManualStrong> o produto some das listas do canal, da fila "Para fazer hoje" e dos
                  contadores (Total e Pendentes), em todos os canais. Ninguém consegue publicá-lo enquanto estiver arquivado. Se
                  ele estava na loja, <ManualStrong>sai da loja em todos os canais</ManualStrong> (no atacado vira rascunho; no
                  Mercado Livre o anúncio é pausado ou fechado, conforme a configuração do canal).
                </>,
                <>
                  <ManualStrong>Como achar um arquivado:</ManualStrong> na lista do canal, <ManualStrong>Filtros ▾ → Situação →
                  Arquivados</ManualStrong>. A lista mostra <ManualStrong>quem arquivou e quando</ManualStrong>. A busca do topo
                  também encontra o produto, com o selo <ManualStrong>Arquivado</ManualStrong>.
                </>,
                <>
                  <ManualStrong>Como desarquivar:</ManualStrong> abra o produto e clique em{" "}
                  <ManualStrong>Desarquivar</ManualStrong> (ou volte a Situação para <ManualStrong>Em operação</ManualStrong>). Ele
                  reaparece nas listas, na fila e nos contadores como <ManualStrong>rascunho</ManualStrong>.{" "}
                  <ManualStrong>No atacado ele não volta sozinho para a loja:</ManualStrong> clique em{" "}
                  <ManualStrong>Publicar</ManualStrong> quando quiser. No Mercado Livre a publicação automática volta a avaliar o
                  produto pela regra normal.
                </>,
                <>
                  <ManualStrong>Depois de desarquivar</ManualStrong> o produto fica como <ManualStrong>Pendente</ManualStrong> até o
                  próximo envio à loja. É esperado: ele precisa ser enviado de novo.
                </>,
              ]}
            />
          </ManualSection>

          <ManualSection id="acompanhar" title="9. Avisos e histórico">
            <ManualUl
              items={[
                <>
                  <ManualStrong>Sino de avisos</ManualStrong> (cabeçalho): o número mostra quantos
                  avisos você ainda não leu. Cada aviso diz o que aconteceu, por exemplo{" "}
                  <ManualStrong>Novo SKU</ManualStrong>, <ManualStrong>Foto inválida</ManualStrong>,{" "}
                  <ManualStrong>Cor sem correspondência</ManualStrong>,{" "}
                  <ManualStrong>Falha de sincronização</ManualStrong>,{" "}
                  <ManualStrong>Conflito de variação</ManualStrong>,{" "}
                  <ManualStrong>Promoção expirada</ManualStrong> ou{" "}
                  <ManualStrong>Produto desativado</ManualStrong>. Clique em{" "}
                  <ManualStrong>Marcar todas como lidas</ManualStrong> pra zerar.
                </>,
                <>
                  <ManualStrong>Histórico:</ManualStrong> no topo da tela do produto. Mostra as
                  versões salvas, da mais recente pra mais antiga.
                </>,
                <>
                  <ManualStrong>Desfazer uma mudança:</ManualStrong> no Histórico, clique em{" "}
                  <ManualStrong>Reverter</ManualStrong> na versão que quer de volta. Reverter não publica
                  sozinho: confira o produto e clique em <ManualStrong>Republicar</ManualStrong>.
                </>,
              ]}
            />
          </ManualSection>

          <ManualSection id="perfis" title="10. Perfis e acessos">
            <p className="text-md text-ink">
              Cada usuário tem um <ManualStrong>perfil</ManualStrong> (ex: operador, admin) e cada
              perfil libera um conjunto de ações: ver, editar, publicar, mexer em cadastros etc. O
              menu mostra só o que o seu perfil permite, e os botões que você não pode usar não
              aparecem.
            </p>
            <ManualUl
              items={[
                <>
                  Se a tela do produto mostrar <ManualStrong>"Somente leitura: seu perfil permite ver
                  este produto."</ManualStrong>, você pode consultar mas não editar.
                </>,
                <>Cada operador só enxerga os canais liberados pra ele.</>,
                <>
                  Precisa de um acesso que não tem? Peça ao super admin. Ele configura perfis e
                  permissões em <ManualStrong>Perfis e acessos</ManualStrong> (<ManualCode>/admin/perfis</ManualCode>).
                </>,
                <>
                  O cadastro de <ManualStrong>Instruções de lavagem</ManualStrong> fica em{" "}
                  <ManualCode>/admin/lavagem</ManualCode>, no menu Administração.
                </>,
                <>
                  O cadastro de <ManualStrong>Guias de tamanho</ManualStrong> fica em{" "}
                  <ManualCode>/admin/guias-de-tamanho</ManualCode>, no menu Administração: título,
                  unidade, tabela de medidas (colunas e linhas), foto de medição e as categorias
                  vinculadas. O bloco <ManualStrong>Como tirar medidas</ManualStrong>, no topo da
                  mesma tela, é global — vale para todos os guias.
                </>,
              ]}
            />
          </ManualSection>

          <ManualSection id="mercado-livre" title="11. Mercado Livre">
            <p className="text-md text-ink">
              O canal do Mercado Livre funciona como os outros: você publica pela ficha do produto. O que muda é a preparação
              (feita uma vez) e algumas exigências do Mercado Livre.
            </p>
            <h3 className="text-md font-extrabold text-ink">Conectar o canal (uma vez, o administrador)</h3>
            <ManualOl
              items={[
                <>Abra <ManualStrong>Admin → Frentes &amp; Canais</ManualStrong> e ache o canal do Mercado Livre.</>,
                <>Se aparecer "Falta o Client Secret", clique em <ManualStrong>Informar Client Secret</ManualStrong>, cole a chave do app
                  "Vitrine Hiit" (use o olho para conferir o que digitou) e salve.</>,
                <>Clique em <ManualStrong>Conectar ao Mercado Livre</ManualStrong> e entre com a conta de administrador da HIITFITWEAR. Ao
                  voltar, o cartão mostra "Conectado como HIITFITWEAR".</>,
                <>O acesso se renova sozinho. Se o cartão mostrar "Desconectado", clique em <ManualStrong>Reconectar</ManualStrong>.</>,
              ]}
            />
            <h3 className="text-md font-extrabold text-ink">Mapear as categorias (uma vez por categoria)</h3>
            <ManualOl
              items={[
                <>Abra o canal do Mercado Livre e clique na aba <ManualStrong>Regras do Mercado Livre</ManualStrong>. O mapa fica no fim da aba,
                  em <ManualStrong>Categorias, ficha técnica e tamanhos</ManualStrong> (a explicação de cada regra está na seção 12).</>,
                <>Escolha uma categoria, clique em <ManualStrong>Sugerir</ManualStrong> e use a sugestão do Mercado Livre que combina
                  com o produto. Complete gênero, faixa etária, embalagem padrão e as medidas da tabela de medidas, e salve.</>,
                <>O aviso no topo diz quantos produtos prontos ainda estão sem categoria. Esses não são publicados.</>,
              ]}
            />
            <h3 className="text-md font-extrabold text-ink">Publicar um produto</h3>
            <ManualUl
              items={[
                <>O produto precisa estar ativo no Sisplan, ter foto do canal, ter preço na tabela de varejo e ter categoria mapeada.</>,
                <>Na ficha do canal do Mercado Livre, confira os campos do canal: título (até 60 caracteres, sem a cor), gênero, faixa
                  etária e a embalagem (altura, largura, comprimento em cm e peso em g, números inteiros). Vazio, vale o da categoria.</>,
                <>Clique em <ManualStrong>Publicar</ManualStrong>. A faixa no alto da ficha mostra o estado do anúncio e o link para
                  abri-lo no Mercado Livre.</>,
              ]}
            />
            <h3 className="text-md font-extrabold text-ink">O que o Mercado Livre costuma recusar</h3>
            <ManualUl
              items={[
                <>Título grande demais ou com palavra proibida; produto sem código de barras (EAN) quando a categoria exige; embalagem sem medida.</>,
                <>Tamanho que não existe na lista do Mercado Livre (peça ao admin um apelido de tamanho) e guia de tamanhos sem as colunas ligadas.</>,
                <>Foto fora do padrão (fundo, proporção). O anúncio pode ser pausado; o motivo chega em Avisos e na ficha.</>,
              ]}
            />
            <h3 className="text-md font-extrabold text-ink">Avisos, pedidos e estoque</h3>
            <ManualUl
              items={[
                <>Pedido novo, pergunta, mensagem e reclamação chegam no sininho com um link para o painel do Mercado Livre. Responder
                  e atender é lá, por enquanto.</>,
                <>A etiqueta de envio só sai depois da NF-e. Emita a nota no Sisplan assim que aparecer o pedido.</>,
                <>O estoque do Mercado Livre começa apenas sendo comparado com o do Sisplan, sem escrever. Quando o admin liga a escrita, o
                  Vitrine envia só o que mudou e desconta os pedidos pagos que ainda não foram despachados, para não vender duas vezes a
                  mesma peça.</>,
              ]}
            />
            <ManualTip>
              Anúncio encerrado no Mercado Livre não volta: publicar de novo cria um anúncio novo. Para tirar um produto do ar sem perder
              o anúncio, use <ManualStrong>Despublicar</ManualStrong> (ele pausa).
            </ManualTip>
          </ManualSection>

          <ManualSection id="ml-equipe" title="12. Mercado Livre para a equipe">
            <p className="text-md text-ink">
              Todas as regras do Mercado Livre ficam na aba <ManualStrong>Regras do Mercado Livre</ManualStrong>, na página do canal. É como no
              WooCommerce: o que a loja decide, vocês mudam na tela, sem pedir para ninguém mexer no sistema. Quem tem o perfil Admin vê e
              salva tudo. Cada cartão tem o seu botão <ManualStrong>Salvar</ManualStrong>, e salvar uma regra não republica nada sozinho:
              os produtos pegam a regra nova na próxima publicação ou republicação.
            </p>

            <h3 className="text-md font-extrabold text-ink">De onde vem cada dado do anúncio</h3>
            <p className="text-md text-ink">
              O Vitrine preenche o anúncio nesta ordem, e o primeiro que tiver valor ganha:
            </p>
            <ManualOl
              items={[
                <><ManualStrong>Escrito neste canal</ManualStrong>: o que você escreve na ficha do produto, no canal do Mercado Livre, com "Sobrescrever" ligado.</>,
                <><ManualStrong>Cadastro do produto</ManualStrong>: o nome principal, a composição e a descrição que vocês já cadastraram.</>,
                <><ManualStrong>Sisplan</ManualStrong>: o público (feminino, masculino, infantil) e a marca de cada peça.</>,
                <><ManualStrong>Categoria</ManualStrong>: o que foi definido para a categoria do Mercado Livre.</>,
                <><ManualStrong>Padrão do canal</ManualStrong>: o que está nos cartões "Padrões" da aba.</>,
              ]}
            />
            <p className="text-md text-ink">
              Na ficha do produto, o cartão <ManualStrong>Ficha técnica no Mercado Livre</ManualStrong> mostra cada campo com o valor que vai e a
              etiqueta de onde ele vem. Para mudar só naquele produto, ligue <ManualStrong>Sobrescrever</ManualStrong>.
            </p>

            <h3 className="text-md font-extrabold text-ink">Os cartões da aba, um por um</h3>
            <ManualUl
              items={[
                <><ManualStrong>Publicar sozinho</ManualStrong>: liga e desliga a publicação automática (produto ativo, com estoque, preço e foto sobe
                  sem ninguém pedir). Também define depois de quantos minutos sem estoque o anúncio é pausado e de qual canal copiar as fotos
                  e a capa.</>,
                <><ManualStrong>Padrões quando nada mais preenche</ManualStrong>: gênero, faixa etária, tipo de anúncio (Clássico ou Premium) e a
                  embalagem (altura, largura, comprimento e peso) usados quando o produto, o Sisplan e a categoria não dizem nada.</>,
                <><ManualStrong>Marca, garantia e prazo de produção</ManualStrong>: a marca do anúncio, o tipo e o tempo de garantia e o prazo de manuseio
                  em dias (tempo para preparar e despachar; vazio = despacha na hora).</>,
                <><ManualStrong>Tirar do ar e guardar estoque</ManualStrong>: ao tirar um produto do ar, <ManualStrong>Pausar</ManualStrong> mantém o anúncio
                  e as vendas dele; <ManualStrong>Encerrar</ManualStrong> é definitivo. E quantas peças ficam de fora do Mercado Livre para não vender o que
                  não tem (vazio usa o valor geral do sistema).</>,
                <><ManualStrong>O que vem do Sisplan</ManualStrong>: como o público do Sisplan vira gênero e faixa etária do Mercado Livre; como a marca do
                  Sisplan vira a marca do anúncio; o tipo da peça que abre o título (código do grupo no Sisplan); e se o modelo nasce do nome do produto.</>,
                <><ManualStrong>Envio</ManualStrong>: o envio é pelo Mercado Envios. Aqui você liga a <ManualStrong>retirada na loja</ManualStrong> (como no
                  site: "Retire na loja, apenas Barueri-SP"; começa desligada até confirmar o endereço de retirada na conta do Mercado Livre) e, se quiser,
                  o frete grátis da loja abaixo de R$ 79. A partir de R$ 79 o Mercado Livre obriga o
                  frete grátis e a regra dele vale sempre; por isso a regra do site (frete grátis acima de R$ 399) não vale no Mercado Livre.</>,
                <><ManualStrong>Código de barras (EAN)</ManualStrong>: se o EAN do Sisplan vai como código de barras do anúncio. Os EANs de hoje começam com
                  777; o Mercado Livre não recusou nos testes. Se um dia recusar, desligue aqui e o anúncio sobe "sem código".</>,
                <><ManualStrong>Cores</ManualStrong>: a "cor principal" do Mercado Livre (lista de 16 cores) de cada cor do Sisplan. Cor em branco sobe sem o
                  filtro de cor.</>,
                <><ManualStrong>Como a IA escreve para este canal</ManualStrong>: a instrução fixa dos botões Gerar com IA do Mercado Livre (tom de varejo, sem
                  inventar tecido nem medida).</>,
                <><ManualStrong>Categorias, ficha técnica e tamanhos</ManualStrong>: a categoria do Mercado Livre de cada categoria do Vitrine, os valores fixos
                  da categoria (ex.: Tipo de roupa = Regata, escolhidos na lista oficial), a tabela de medidas e os apelidos de tamanho. A lista "Produtos que
                  caem nesta categoria" avisa quando uma categoria cobre tipos diferentes (ex.: acessórios virando tiara): crie uma subcategoria para cada tipo.</>,
              ]}
            />
            <p className="text-md text-ink">
              O bloco <ManualStrong>Avançado (técnico)</ManualStrong> no fim da aba (só quem cuida do sistema) guarda a frequência e os limites dos processos
              automáticos. O sistema funciona com os valores atuais.
            </p>

            <h3 className="text-md font-extrabold text-ink">Título, descrição e IA</h3>
            <ManualUl
              items={[
                <>O <ManualStrong>título</ManualStrong> nasce do nome principal do produto: sem símbolos, em letras normais, com o tipo da peça no começo quando
                  o nome está todo em maiúsculas, até 60 caracteres. Para escrever o seu, ligue "Sobrescrever" no Nome do canal.</>,
                <>A <ManualStrong>descrição</ManualStrong> do Mercado Livre é texto simples: sem negrito, link, telefone nem emoji. As tecnologias e os cuidados
                  do produto são acrescentados sozinhos.</>,
                <>Os botões <ManualStrong>Gerar com IA</ManualStrong> só sugerem: nada é salvo até você revisar e salvar. Há botão para o título, a descrição, o
                  modelo e para os atributos da categoria (a IA só escolhe valores que existem na lista do Mercado Livre).</>,
                <>A <ManualStrong>composição</ManualStrong> (ex.: 94% viscose, 6% elastano) é dado de etiqueta: vocês escrevem, a IA não inventa. A fibra de maior
                  percentual vira o material principal do anúncio.</>,
                <>Promoção, SEO, tags e descrição curta não existem no Mercado Livre e por isso não aparecem na ficha dele.</>,
              ]}
            />
            <ManualTip>
              Produto parado na lista de pendências? O motivo aparece na aba Produtos do canal, no painel de pendências. As causas mais comuns são sem preço no varejo, sem foto do canal,
              categoria sem mapa e tabela de medidas desligada para um tipo de produto que o Mercado Livre exige.
            </ManualTip>
          </ManualSection>

          <ManualSection id="duvidas" title="13. Dúvidas frequentes">
            <ManualFaq
              items={[
                {
                  q: "Subi a foto e ela não apareceu.",
                  a: (
                    <>
                      Espere até 7 minutos. Se não aparecer, confira se o arquivo é JPG, PNG ou WebP de verdade
                      (não HEIC) e, se foi pelo Drive, se está na pasta certa (<ManualCode>01 - Atacado/{"<código> - <nome>"}</ManualCode>,
                      ou na subpasta da cor). Veja também o sino: pode ter chegado um aviso de{" "}
                      <ManualStrong>Foto inválida</ManualStrong> ou <ManualStrong>Cor sem correspondência</ManualStrong>.
                    </>
                  ),
                },
                {
                  q: "A foto apareceu na cor errada.",
                  a: (
                    <>
                      Abra o <ManualStrong>⋯</ManualStrong> da foto, em <ManualStrong>Ações da foto</ManualStrong>{" "}
                      marque a cor certa e clique em <ManualStrong>Aplicar</ManualStrong>. Depois, republique.
                    </>
                  ),
                },
                {
                  q: "O produto sumiu da loja.",
                  a: (
                    <>
                      Duas causas comuns: (1) alguém despublicou: abra o produto e veja o status no
                      rodapé; se estiver <ManualStrong>Rascunho</ManualStrong>, clique em{" "}
                      <ManualStrong>Publicar</ManualStrong>. (2) O produto foi{" "}
                      <ManualStrong>desativado no Sisplan</ManualStrong>: o sistema tira da loja sozinho
                      (em até ~15 minutos) e avisa no sino. Se ele voltar a ficar ativo no Sisplan, a
                      volta à loja não é automática: abra o produto e clique em Publicar.
                    </>
                  ),
                },
                {
                  q: "O botão Publicar está bloqueado.",
                  a: (
                    <>
                      Olhe o <ManualStrong>"Falta: …"</ManualStrong> no rodapé fixo. Ele diz exatamente o que
                      falta (foto, descrição longa ou categoria).
                    </>
                  ),
                },
                {
                  q: "Troquei a foto, o texto ou os tamanhos, mas na loja continua o antigo.",
                  a: (
                    <>
                      Clique em <ManualStrong>Republicar</ManualStrong> pra enviar a versão mais nova.
                    </>
                  ),
                },
                {
                  q: "A publicação falhou.",
                  a: (
                    <>
                      Aparece um aviso de <ManualStrong>Falha de sincronização</ManualStrong> no sino.
                      Espere um minuto e clique em <ManualStrong>Republicar</ManualStrong>. Se falhar de
                      novo, avise o admin com o código do produto.
                    </>
                  ),
                },
                {
                  q: '"Gerar com IA" mostrou um erro em vermelho.',
                  a: (
                    <>
                      A IA usa um serviço externo com limite de uso diário. O texto em vermelho diz o
                      motivo. Não é erro seu: escreva o texto à mão ou tente de novo mais tarde.
                    </>
                  ),
                },
                {
                  q: "Não sei minha senha / esqueci a senha.",
                  a: (
                    <>
                      Não dá pra recuperar sozinho. Peça pro admin resetar em{" "}
                      <ManualCode>/admin/usuarios</ManualCode>.
                    </>
                  ),
                },
                {
                  q: "A descrição ficou com código estranho tipo <p> no meio do texto.",
                  a: (
                    <>
                      Acontece em produtos antigos com formatação especial (negrito, listas). Se quiser
                      simplificar, apague tudo e digite de novo um texto normal com parágrafos.
                    </>
                  ),
                },
                {
                  q: "Não vejo um canal, um menu ou um botão que outra pessoa vê.",
                  a: (
                    <>
                      É o seu perfil. Cada perfil libera um conjunto de ações e canais. Peça ao super
                      admin se precisar de mais acesso.
                    </>
                  ),
                },
                {
                  q: "Publiquei e a categoria ficou errada na loja.",
                  a: (
                    <>
                      Confira a categoria na Base (ou se está ligada a chave{" "}
                      <ManualStrong>Sobrescrever</ManualStrong> no canal) e clique em{" "}
                      <ManualStrong>Republicar</ManualStrong>. Se o problema persistir, avise o admin.
                    </>
                  ),
                },
                {
                  q: "De onde vem o preço e o estoque do produto na loja?",
                  a: (
                    <>
                      Do <ManualStrong>Sisplan</ManualStrong>, automaticamente. Ninguém digita preço
                      normal nem quantidade aqui. Pra mudar, mude no Sisplan; a loja atualiza na próxima
                      publicação ou republicação. O único preço que se define aqui é a promoção.
                    </>
                  ),
                },
              ]}
            />
          </ManualSection>

          <p className="field__hint pt-2">
            Dúvidas que não estão aqui: fale com o Wladimir.
          </p>
        </div>
      </div>
    </div>
  );
}

function ManualSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}): JSX.Element {
  const match = /^(\d+)\.\s*(.+)$/.exec(title);
  const n = match?.[1] ?? "";
  const rest = match?.[2] ?? title;
  return (
    <Card id={id} className="scroll-mt-4 cluster" style={{ alignItems: "flex-start" }}>
      <span
        aria-hidden
        style={{
          width: "1.75rem",
          height: "1.75rem",
          borderRadius: "var(--r-md)",
          background: "var(--coral-soft)",
          color: "var(--coral)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: "var(--fw-black)",
          fontSize: "var(--fs-base)",
          flexShrink: 0,
        }}
      >
        {n}
      </span>
      <div className="stack grow" style={{ gap: "var(--sp-3)", minWidth: 0 }}>
        <h2 style={{ margin: 0, fontSize: "var(--fs-xl)", fontWeight: "var(--fw-black)" }}>{rest}</h2>
        {children}
      </div>
    </Card>
  );
}

function ManualOl({ items }: { items: ReactNode[] }): JSX.Element {
  return (
    <ol className="list-decimal list-inside text-md text-ink marker:text-ink-3">
      {items.map((it, i) => (
        <li key={i} className={i > 0 ? "mt-1.5" : undefined}>{it}</li>
      ))}
    </ol>
  );
}

function ManualUl({ items }: { items: ReactNode[] }): JSX.Element {
  return (
    <ul className="list-disc list-inside text-md text-ink marker:text-ink-3">
      {items.map((it, i) => (
        <li key={i} className={i > 0 ? "mt-1.5" : undefined}>{it}</li>
      ))}
    </ul>
  );
}

function ManualStrong({ children }: { children: ReactNode }): JSX.Element {
  return <strong className="font-extrabold text-ink">{children}</strong>;
}

function ManualCode({ children }: { children: ReactNode }): JSX.Element {
  return (
    <code
      className="mono text-ink"
      style={{ background: "var(--color-soft)", padding: "0 .375rem", borderRadius: "var(--radius-sm)", fontSize: "var(--text-sm)" }}
    >
      {children}
    </code>
  );
}

function ManualLink({ href, children }: { href: string; children: ReactNode }): JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-coral underline underline-offset-2 hover:no-underline"
    >
      {children}
    </a>
  );
}

function ManualTip({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div
      className="text-md text-ink"
      style={{ border: "var(--border)", background: "var(--color-soft)", borderRadius: "var(--radius-md)", padding: "var(--sp-3)" }}
    >
      {children}
    </div>
  );
}

function ManualFaq({ items }: { items: { q: string; a: ReactNode }[] }): JSX.Element {
  return (
    <div className="stack" style={{ gap: "var(--sp-3)" }}>
      {items.map((it, i) => (
        <div key={i} style={{ border: "var(--border)", borderRadius: "var(--radius-md)", padding: "var(--sp-3)" }}>
          <p className="text-md font-extrabold text-ink">{it.q}</p>
          <p className="mt-1 field__hint">{it.a}</p>
        </div>
      ))}
    </div>
  );
}
